<script setup>
import { Head, useForm } from '@inertiajs/vue3'
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import { platformLabel as platformName } from '@/lib/platformLabel'
import InquireForm from '@/Components/Public/InquireForm.vue'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'
import PublicGallery from '@/Components/Public/PublicGallery.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'
import ProductRail from '@/Components/Public/ProductRail.vue'
import BookingRequestForm from '@/Components/Public/BookingRequestForm.vue'
import PaymentMethods from '@/Components/Public/PaymentMethods.vue'
import MobileBar from '@/Components/Public/MobileBar.vue'
import PriceTierHints from '@/Components/Public/PriceTierHints.vue'
import CheckoutCard from '@/Components/Public/CheckoutCard.vue'
import { usePublicMoney } from '@/composables/usePublicMoney'
import { usePublicDate } from '@/composables/usePublicDate'
import { usePriceQuote } from '@/composables/usePriceQuote'
import DateField from '@/Components/Public/DateField.vue'

defineOptions({ layout: PublicLayout })

const props = defineProps({
  vehicle:          { type: Object,  required: true },
  base_path:        { type: String,  default: '/rentals' },
  related:          { type: Array,   default: () => [] },
  review_invite:    { type: Object,  default: null },
  checkout_enabled: { type: Boolean, default: false }
})
const { t } = useI18n()

const photos = props.vehicle.photos?.length ? props.vehicle.photos : []
const eyebrow = computed(() => `${t(`public.classes.${props.vehicle.vehicle_class}`)} · ${t(`public.categories.${props.vehicle.category}`)}`)
const title = computed(() => `${props.vehicle.make} ${props.vehicle.model}${props.vehicle.year ? ` (${props.vehicle.year})` : ''}`)

// "Plan your rental" booking helper — mirrors the tour page's plan card:
// pickup/return dates drive a live estimate that pre-fills the chat CTAs.
const { formatMoney } = usePublicMoney()
const { formatDate } = usePublicDate()
const today = computed(() => new Date().toLocaleDateString('en-CA')) // YYYY-MM-DD, local
const pickupDate = ref('')
const returnDate = ref('')

const rentalDays = computed(() => {
  if (!pickupDate.value || !returnDate.value) return 0
  const ms = new Date(returnDate.value) - new Date(pickupDate.value)
  if (ms < 0) return 0
  return Math.max(1, Math.round(ms / 86400000))
})
// Local math is the pre-server-quote placeholder only: shown while the
// debounced server quote (the authoritative PriceQuote.for total, which
// reflects any assigned rate-plan tier) is in flight or hasn't landed yet.
const estimateCents = computed(() => rentalDays.value * (props.vehicle.daily_rate_cents || 0))
const { quote, loading: quoteLoading, schedule: scheduleQuote } = usePriceQuote('vehicle', props.vehicle.id)
watch([ pickupDate, returnDate ], () => {
  // Price-on-request VIP listings never have a numeric total — /quote
  // itself 404s for these (Public::QuotesController#show), so skip the
  // fetch entirely rather than firing a call that can only fail.
  if (props.vehicle.price_on_request || props.vehicle.unclaimed) return
  scheduleQuote(pickupDate.value && returnDate.value ? { start_on: pickupDate.value, end_on: returnDate.value } : null)
}, { immediate: true })
const estimateDisplay = computed(() => {
  if (!quoteLoading.value && quote.value) return formatMoney(quote.value.total_cents)
  return formatMoney(estimateCents.value)
})
const daysLabel = computed(() => t('public.catalog.booking.days', { n: rentalDays.value }, rentalDays.value))

const bookingMessage = computed(() => {
  if (rentalDays.value > 0) {
    return t('public.catalog.booking.message', {
      vehicle: title.value,
      from: formatDate(pickupDate.value),
      to: formatDate(returnDate.value),
      days: daysLabel.value,
      total: estimateDisplay.value
    })
  }
  return t('public.catalog.booking.message_nodate', { vehicle: title.value })
})

// Reviews — mirrors the tour page's moderate-before-display flow.
const showAllReviews = ref(false)
const visibleReviews = computed(() =>
  showAllReviews.value ? props.vehicle.reviews?.items : props.vehicle.reviews?.items?.slice(0, 5)
)

const reviewForm = useForm({
  author_name: props.review_invite?.author_name || '',
  rating: 5, body: '', email: '', website: '', photos: []
})
const reviewSubmitted = ref(false)
const photoPreviews = ref([])

function onPhotoChange(e) {
  const files = Array.from(e.target.files || []).slice(0, 5)
  reviewForm.photos = files
  photoPreviews.value.forEach(u => URL.revokeObjectURL(u))
  photoPreviews.value = files.map(f => URL.createObjectURL(f))
}

function submitReview() {
  reviewForm.transform(d => ({
    review: { author_name: d.author_name, rating: d.rating, body: d.body, email: d.email, photos: d.photos },
    website: d.website,
    invite: props.review_invite?.token || undefined
  })).post(props.vehicle.reviews_path, {
    preserveScroll: true,
    forceFormData: true,
    onSuccess: () => { reviewSubmitted.value = true }
  })
}

// Rewrite the booking message into text-prefill chat URLs (those with a ?text=
// param); pass start-param links (Telegram) through unchanged.
const bookingIntents = computed(() => {
  const out = {}
  for (const [platform, url] of Object.entries(props.vehicle.contact_intents || {})) {
    try {
      const u = new URL(url)
      if (u.searchParams.has('text')) { u.searchParams.set('text', bookingMessage.value); out[platform] = u.toString() }
      else out[platform] = url
    } catch { out[platform] = url }
  }
  return out
})


// One "Book via …" button per chat channel, in the server's locale-aware
// order (lead platform first, then the operator's Settings order).
const bookingCtas = computed(() =>
  Object.entries(bookingIntents.value)
    .map(([platform, url]) => ({ platform, url, name: platformName(platform) }))
)
</script>

<template>
  <Head>
    <title>{{ vehicle.make }} {{ vehicle.model }} — {{ t('public.catalog.head_title') }}</title>
  </Head>

  <BrandHero kind="vehicle" :eyebrow="eyebrow" :title="title">
    <template #top>
      <a :href="base_path" class="bhero-back">← {{ t('public.catalog.back') }}</a>
    </template>
    <span v-if="vehicle.verified" class="bhero-verified">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" /></svg>{{ t('public.verified') }}
    </span>
    <span v-if="vehicle.unclaimed" class="bhero-unclaimed">{{ t('public.unclaimed.badge') }}</span>
    <p v-if="vehicle.price_on_request" class="bhero-price fw-value">{{ t('public.vip.price_on_request') }}</p>
    <p v-else class="bhero-price"><span v-if="vehicle.unclaimed" class="bhero-from">{{ t('public.unclaimed.from') }} </span><span class="fw-value">{{ vehicle.daily_rate }}</span> <span class="bhero-per">{{ t('public.catalog.per_day') }}</span></p>
    <p v-if="vehicle.deposit" class="bhero-deposit">{{ t('public.catalog.deposit') }}: <span class="fw-value">{{ vehicle.deposit }}</span></p>
    <p class="bhero-trust">✓ {{ t('public.trust.free_cancellation_vehicle') }}</p>
    <p class="bhero-trust" :title="t('public.delivery.honesty')">🛵 {{ t('public.delivery.vehicle_line') }}</p>
    <div class="bhero-ctas">
      <LuxButton
        v-for="(url, platform, idx) in vehicle.contact_intents"
        :key="platform"
        :href="url"
        :variant="idx === 0 ? 'solid' : 'line'"
        tone="dark"
        :glyph="platform"
        target="_blank"
        rel="noopener"
      >{{ t('public.write_on', { platform: platformName(platform) }) }}</LuxButton>
      <LuxButton href="#inquire" variant="line" tone="dark" glyph="arrow">{{ t('public.catalog.ctas.form') }}</LuxButton>
    </div>
  </BrandHero>

  <section class="body">
    <div class="body__inner">
      <p v-if="vehicle.unclaimed" class="unclaimed-notice">{{ t('public.unclaimed.notice') }}</p>
      <a v-if="vehicle.unclaimed && vehicle.claim_url" :href="vehicle.claim_url" class="unclaimed-claim">{{ t('public.unclaimed.claim_cta') }}</a>
      <PublicGallery :photos="photos" :alt="`${vehicle.make} ${vehicle.model}`">
        <template #placeholder>
          <CatalogPlaceholder kind="vehicle" :category="vehicle.category" />
        </template>
      </PublicGallery>

      <div class="detail">
        <div class="detail__content">
          <section v-if="vehicle.highlights?.length" class="card">
            <h2 class="section-title">{{ t('public.catalog.sections.highlights') }}</h2>
            <ul class="flist flist--star"><li v-for="(x, i) in vehicle.highlights" :key="i">{{ x }}</li></ul>
          </section>

          <section v-if="vehicle.description" class="card">
            <h2 class="section-title">{{ t('public.catalog.sections.overview') }}</h2>
            <p class="prose">{{ vehicle.description }}</p>
          </section>

          <section v-if="vehicle.features?.length" class="card">
            <h2 class="section-title">{{ t('public.catalog.sections.features') }}</h2>
            <ul class="flist"><li v-for="(x, i) in vehicle.features" :key="i">{{ x }}</li></ul>
          </section>

          <section v-if="vehicle.inclusions?.length" class="card">
            <h2 class="section-title">{{ t('public.catalog.sections.included') }}</h2>
            <ul class="flist flist--yes"><li v-for="(x, i) in vehicle.inclusions" :key="i">{{ x }}</li></ul>
          </section>

          <section v-if="vehicle.requirements?.length" class="card">
            <h2 class="section-title">{{ t('public.catalog.sections.requirements') }}</h2>
            <ul class="flist flist--req"><li v-for="(x, i) in vehicle.requirements" :key="i">{{ x }}</li></ul>
          </section>

          <section v-if="vehicle.good_to_know?.length" class="card">
            <h2 class="section-title">{{ t('public.catalog.sections.good_to_know') }}</h2>
            <ul class="flist flist--info"><li v-for="(x, i) in vehicle.good_to_know" :key="i">{{ x }}</li></ul>
          </section>

          <!-- Reviews -->
          <section id="reviews" class="card">
            <h2 class="section-title">{{ t('public.catalog.reviews.title') }}</h2>

            <div v-if="vehicle.reviews?.count > 0" class="reviews__header">
              <span class="reviews__average fw-value">{{ Number(vehicle.reviews.average).toFixed(1) }}</span>
              <span class="reviews__stars" aria-hidden="true">
                <span v-for="n in 5" :key="n" class="review-star" :style="{ color: n <= Math.round(vehicle.reviews.average) ? 'var(--warn)' : 'var(--line)' }">★</span>
              </span>
              <span class="reviews__count">{{ t('public.catalog.reviews.count', { n: vehicle.reviews.count }, vehicle.reviews.count) }}</span>
            </div>

            <p v-if="vehicle.reviews?.count === 0" class="reviews__empty prose">{{ t('public.catalog.reviews.empty') }}</p>

            <ul v-if="vehicle.reviews?.count > 0" class="reviews__list">
              <li v-for="(item, i) in visibleReviews" :key="i" class="review-item">
                <div class="review-item__meta">
                  <span class="review-item__stars" aria-hidden="true">
                    <span v-for="n in 5" :key="n" class="review-star" :style="{ color: n <= item.rating ? 'var(--warn)' : 'var(--line)' }">★</span>
                  </span>
                  <span class="review-item__author fw-value">{{ item.author_name }}</span>
                  <span v-if="item.verified" class="review-item__verified">✓ {{ t('public.reviews.verified_renter') }}</span>
                  <span class="review-item__date">{{ formatDate(item.date) }}</span>
                </div>
                <p class="review-item__body prose">{{ item.body }}</p>
                <div v-if="item.photos?.length" class="review-item__photos">
                  <a v-for="(ph, pi) in item.photos" :key="pi" :href="ph.full" target="_blank" rel="noopener">
                    <img :src="ph.thumb" :alt="t('public.reviews.photo_alt')" loading="lazy">
                  </a>
                </div>
              </li>
            </ul>

            <button
              v-if="vehicle.reviews?.count > 5 && !showAllReviews"
              class="reviews__more"
              type="button"
              @click="showAllReviews = true"
            >{{ t('public.catalog.reviews.show_more') }}</button>

            <div class="reviews__form-wrap">
              <h3 class="reviews__form-title">{{ t('public.catalog.reviews.write') }}</h3>

              <div v-if="review_invite && !review_invite.already_reviewed && !reviewSubmitted" class="reviews__invite">
                ✓ {{ t('public.reviews.invite_banner') }}
              </div>

              <div v-if="review_invite?.already_reviewed" class="reviews__thanks">
                {{ t('public.reviews.already_reviewed') }}
              </div>

              <div v-else-if="reviewSubmitted" class="reviews__thanks">
                {{ t('public.catalog.reviews.thanks') }}
              </div>

              <form v-else class="reviews__form" @submit.prevent="submitReview">
                <input
                  v-model="reviewForm.website"
                  type="text"
                  name="website"
                  tabindex="-1"
                  autocomplete="off"
                  class="reviews__honeypot"
                  aria-hidden="true"
                >

                <label class="review-field">
                  <span class="review-field__label">{{ t('public.catalog.reviews.name') }}</span>
                  <input
                    v-model="reviewForm.author_name"
                    type="text"
                    class="review-field__input"
                    required
                    maxlength="80"
                  >
                </label>

                <div class="review-field">
                  <span class="review-field__label">{{ t('public.catalog.reviews.rating') }}</span>
                  <div class="star-picker" role="radiogroup" :aria-label="t('public.catalog.reviews.rating')">
                    <button
                      v-for="n in 5"
                      :key="n"
                      type="button"
                      class="star-picker__btn"
                      :aria-label="t('public.reviews.star_label', { n }, n)"
                      :aria-pressed="reviewForm.rating === n"
                      :style="{ color: n <= reviewForm.rating ? 'var(--warn)' : 'var(--line)' }"
                      @click="reviewForm.rating = n"
                    >★</button>
                  </div>
                </div>

                <label class="review-field">
                  <span class="review-field__label">{{ t('public.catalog.reviews.text') }}</span>
                  <textarea
                    v-model="reviewForm.body"
                    class="review-field__input review-field__textarea"
                    required
                    minlength="3"
                    maxlength="2000"
                    rows="4"
                  />
                </label>

                <label class="review-field">
                  <span class="review-field__label">{{ t('public.catalog.reviews.email') }}</span>
                  <input
                    v-model="reviewForm.email"
                    type="email"
                    class="review-field__input"
                    autocomplete="email"
                  >
                </label>

                <label class="review-field">
                  <span class="review-field__label">{{ t('public.reviews.add_photos') }}</span>
                  <input
                    type="file"
                    class="review-field__input review-field__file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    @change="onPhotoChange"
                  >
                  <span class="review-field__hint">{{ t('public.reviews.photos_hint') }}</span>
                </label>
                <div v-if="photoPreviews.length" class="review-photos-preview">
                  <img v-for="(src, i) in photoPreviews" :key="i" :src="src" alt="">
                </div>

                <button type="submit" class="reviews__submit" :disabled="reviewForm.processing">
                  {{ t('public.catalog.reviews.submit') }}
                </button>
              </form>
            </div>
          </section>

          <div id="inquire" class="inquire">
            <h2 class="inquire__title">{{ t('public.inquire.title') }}</h2>
            <p class="inquire__lede">{{ t('public.inquire.lede') }}</p>
            <InquireForm :topic="'rental'" :vehicle-id="vehicle.id" :prefill-message="`${t('public.inquire.prefill_vehicle')} ${vehicle.make} ${vehicle.model}`" />
          </div>
        </div>

        <aside class="detail__aside">
          <div class="facts">
            <h2 class="section-title">{{ t('public.catalog.sections.at_a_glance') }}</h2>
            <dl class="dl">
              <div v-if="vehicle.engine_cc"><dt>{{ t('public.catalog.specs.engine') }}</dt><dd>{{ vehicle.engine_cc }}cc</dd></div>
              <div v-if="vehicle.transmission"><dt>{{ t('public.catalog.specs.transmission') }}</dt><dd>{{ t(`public.transmissions.${vehicle.transmission}`) }}</dd></div>
              <div v-if="vehicle.fuel_type"><dt>{{ t('public.catalog.specs.fuel') }}</dt><dd>{{ t(`public.fuels.${vehicle.fuel_type}`) }}</dd></div>
              <div v-if="vehicle.seats"><dt>{{ t('public.catalog.specs.seats') }}</dt><dd>{{ vehicle.seats }}</dd></div>
              <div v-if="vehicle.colour"><dt>{{ t('public.catalog.specs.colour') }}</dt><dd>{{ vehicle.colour }}</dd></div>
              <div v-if="vehicle.price_on_request"><dt>{{ t('public.catalog.per_day') }}</dt><dd class="dl__price">{{ t('public.vip.price_on_request') }}</dd></div>
              <div v-else><dt>{{ t('public.catalog.per_day') }}</dt><dd class="dl__price">{{ vehicle.daily_rate }}</dd></div>
              <div v-if="vehicle.deposit"><dt>{{ t('public.catalog.deposit') }}</dt><dd>{{ vehicle.deposit }}</dd></div>
            </dl>
            <PriceTierHints :tiers="vehicle.price_tiers" kind="duration" />
          </div>

          <div class="trust-card">
            <h2 class="section-title">{{ t('public.catalog.trust.title') }}</h2>
            <ul class="trust-list">
              <li>{{ t('public.trust.free_cancellation_vehicle') }}</li>
              <li v-if="vehicle.verified">{{ t('public.catalog.trust.verified') }}</li>
              <li>{{ t('public.catalog.trust.no_payment') }}</li>
              <li v-if="vehicle.deposit">{{ t('public.catalog.trust.deposit_back') }}</li>
              <li>{{ t('public.catalog.trust.local_support') }}</li>
            </ul>
          </div>

          <div id="request-to-book" class="plan-card">
            <h2 class="section-title">{{ t('public.catalog.booking.title') }}</h2>
            <p class="plan-delivery">🛵 {{ t('public.delivery.vehicle_line') }}</p>
            <p class="plan-delivery-honesty">{{ t('public.delivery.honesty') }}</p>
            <label class="plan-row">
              <span class="plan-label">{{ t('public.catalog.booking.pickup') }}</span>
              <DateField v-model="pickupDate" :min="today" class="plan-date" :placeholder="t('public.pick_date')" />
            </label>
            <label class="plan-row">
              <span class="plan-label">{{ t('public.catalog.booking.return') }}</span>
              <DateField v-model="returnDate" :min="pickupDate || today" class="plan-date" :placeholder="t('public.pick_date')" />
            </label>
            <p v-if="vehicle.price_on_request" class="plan-total fw-value">{{ t('public.vip.price_on_request') }}</p>
            <template v-else>
              <p v-if="rentalDays > 0" class="plan-total">
                {{ t('public.catalog.booking.estimate_total') }}
                <span class="fw-value">≈ {{ estimateDisplay }}</span>
                <span class="plan-days">({{ daysLabel }})</span>
              </p>
              <p class="plan-caveat">{{ t('public.catalog.booking.estimate_caveat') }}</p>
            </template>
            <p v-if="vehicle.deposit" class="plan-deposit">{{ t('public.catalog.booking.deposit_note', { amount: vehicle.deposit }) }}</p>
            <h3 class="plan-subtitle">{{ t('public.booking_request.title') }}</h3>
            <p v-if="vehicle.unclaimed" class="unclaimed-hint">{{ t('public.unclaimed.request_hint') }}</p>
            <BookingRequestForm
              bookable-type="vehicle"
              :bookable-id="vehicle.id"
              :details="{ start_on: pickupDate, end_on: returnDate }"
              :disabled="!pickupDate || !returnDate"
              show-delivery
            />
            <p v-if="bookingCtas.length" class="plan-divider">{{ t('public.booking_request.or_chat') }}</p>
            <div v-if="bookingCtas.length" class="plan-ctas">
              <LuxButton v-for="c in bookingCtas" :key="c.platform" :href="c.url" variant="line" tone="light" :glyph="c.platform" block target="_blank" rel="noopener">{{ t('public.catalog.booking.cta_via', { platform: c.name }) }}</LuxButton>
            </div>
            <PaymentMethods inline />
          </div>

          <CheckoutCard
            v-if="checkout_enabled && !vehicle.price_on_request && !vehicle.unclaimed"
            bookable-type="vehicle"
            :bookable-id="vehicle.id"
            :details="{ start_on: pickupDate, end_on: returnDate }"
            :disabled="!pickupDate || !returnDate"
            show-delivery
            :show-deposit-toggle="!!vehicle.deposit"
            :deposit-amount="vehicle.deposit"
            :quote="quote"
            :quote-loading="quoteLoading"
          />
        </aside>
      </div>
    </div>
  </section>

  <section
    v-if="related.length"
    class="related"
  >
    <div class="related__inner">
      <ProductRail
        :title="t('public.catalog.related')"
        kind="vehicle"
        :items="related"
        :item-base="base_path"
        :view-all-href="base_path"
      />
    </div>
  </section>
  <MobileBar target="request-to-book" :value="vehicle.price_on_request ? t('public.vip.price_on_request') : (vehicle.daily_rate || '')" :sub="vehicle.price_on_request ? '' : t('public.catalog.per_day')" :cta="t('public.catalog.booking.title')" />
</template>

<style scoped>
/* Band content (slotted into BrandHero → inherits the band's light text). */
.bhero-back { display: inline-block; margin-bottom: 1rem; color: inherit; opacity: 0.85; text-decoration: none; font-size: 0.95rem; }
.bhero-back:hover { opacity: 1; }
.bhero-verified {
  display: inline-flex; align-items: center; gap: 0.35rem; margin: 1rem 0 0;
  padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.85rem; font-weight: 700;
  background: color-mix(in oklch, currentColor 16%, transparent);
  border: 1px solid color-mix(in oklch, currentColor 35%, transparent);
}
.bhero-verified svg { width: 1rem; height: 1rem; }
.bhero-vip {
  display: inline-flex; align-items: center; margin: 1rem 0.4rem 0 0;
  padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.85rem; font-weight: 700;
  background: var(--ink); color: var(--warn); border: 1px solid var(--warn);
}
.bhero-unclaimed {
  display: inline-flex; align-items: center; margin: 1rem 0.4rem 0 0;
  padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.85rem; font-weight: 700;
  background: color-mix(in oklch, currentColor 12%, transparent);
  border: 1px solid color-mix(in oklch, currentColor 30%, transparent);
  opacity: 0.85;
}
.bhero-price { margin: 1.1rem 0 0; font-size: clamp(1.6rem, 3vw, 2.1rem); }
.bhero-per { font-size: 1rem; opacity: 0.85; }
.bhero-from { font-size: 1rem; opacity: 0.8; }
.bhero-deposit { margin: 0.3rem 0 0; font-size: 1rem; opacity: 0.9; }
.bhero-trust { margin: 0.45rem 0 0; font-size: 0.95rem; font-weight: 600; }
.bhero-ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 1.5rem; }

.body { background: var(--bg); }
.body__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; }


.unclaimed-notice {
  margin: 0 0 0.75rem; padding: 0.85rem 1rem;
  background: var(--surfaceAlt); border: 1px solid var(--line); border-radius: var(--radius-table, 0.75rem);
  color: var(--inkSoft); font-size: 0.9rem; line-height: 1.5;
}
.unclaimed-claim {
  display: inline-block; margin: 0 0 1.5rem; color: var(--accent); font-weight: 700; text-decoration: none; font-size: 0.9rem;
}
.unclaimed-claim:hover { text-decoration: underline; }
.unclaimed-hint { margin: 0.4rem 0 0.75rem; font-size: 0.82rem; color: var(--muted); }

.info__heading { font-family: var(--font-display, var(--display)); font-weight: 700; font-size: 1.2rem; color: var(--ink); margin: 0 0 0.75rem; }
.specs { display: grid; grid-template-columns: 9rem 1fr; gap: 0.5rem 1rem; margin: 0; font-size: 1rem; }
.specs dt { color: var(--muted); margin: 0; }
.specs dd { margin: 0; color: var(--ink); }

.detail { display: grid; grid-template-columns: 1fr; gap: 1.5rem; margin-top: 1.5rem; }
@media (min-width: 880px) { .detail { grid-template-columns: 1.6fr 0.9fr; gap: 2rem; align-items: start; } }
.detail__content { display: grid; gap: 1rem; }
@media (min-width: 880px) { .detail__aside { position: sticky; top: 1.5rem; } }
.facts { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.section-title { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.75rem; }
.prose { color: var(--inkSoft); line-height: 1.6; white-space: pre-wrap; margin: 0; }
.dl { display: grid; gap: 0.55rem; margin: 0; }
.dl > div { display: grid; grid-template-columns: 9rem 1fr; gap: 0.5rem; align-items: baseline; }
.dl dt { color: var(--muted); font-size: 0.875rem; }
.dl dd { margin: 0; color: var(--ink); font-size: 1rem; }
.dl__price { color: var(--accent); font-weight: 700; }
/* Default marker first; the modifier rules below have equal specificity, so
   they win by later source order. A no-modifier list (Features) keeps the dot.
   Keep this exact order. */
.flist { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.5rem; }
.flist li { position: relative; padding-left: 1.6rem; color: var(--inkSoft); line-height: 1.5; }
.flist li::before        { position: absolute; left: 0; top: 0.05rem; font-weight: 700; content: "•"; color: var(--accent); }
.flist--star li::before  { content: "★"; color: var(--accent); }
.flist--yes li::before   { content: "✓"; color: var(--ok); }
.flist--req li::before   { content: "•"; color: var(--accent); }
.flist--info li::before  { content: "ℹ"; color: var(--second); }

.trust-card {
  margin-top: 1.25rem; padding: 1.25rem;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-table, 0.75rem);
}
.trust-list { list-style: none; margin: 0.75rem 0 0; padding: 0; display: grid; gap: 0.6rem; }
.trust-list li {
  position: relative; padding-left: 1.6rem; color: var(--inkSoft); line-height: 1.4;
}
.trust-list li::before {
  content: "✓"; position: absolute; left: 0; top: 0;
  color: var(--ok); font-weight: 700;
}
.plan-card {
  margin-top: 1.25rem; padding: 1.25rem;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-table, 0.75rem);
}
.plan-delivery { margin: 0 0 0.15rem; font-size: 0.9rem; font-weight: 600; color: var(--ok); }
.plan-delivery-honesty { margin: 0 0 0.75rem; font-size: 0.75rem; color: var(--muted); }
.plan-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-top: 0.85rem; }
.plan-label { color: var(--inkSoft); font-size: 0.95rem; }
.plan-date {
  border: 1px solid var(--line); border-radius: var(--radius-input, 0.5rem);
  background: var(--bg); color: var(--ink); padding: 0.4rem 0.6rem; font: inherit;
  color-scheme: light;
}
.plan-total { margin: 1rem 0 0.2rem; font-size: 1.05rem; color: var(--ink); }
.plan-days { color: var(--muted); font-size: 0.9rem; margin-left: 0.3rem; }
.plan-deposit { margin: 0.2rem 0 0; font-size: 0.875rem; color: var(--inkSoft); }
.plan-caveat { margin: 0.3rem 0 0; font-size: 0.8rem; color: var(--muted); }
.plan-subtitle { margin: 1.1rem 0 0; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); }
.plan-divider { margin: 0.9rem 0 0; text-align: center; font-size: 0.8rem; color: var(--muted); }
.plan-ctas { margin-top: 1rem; display: grid; gap: 0.5rem; }

/* Reviews (mirrors Tours/Show) */
.reviews__header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem; }
.reviews__average { font-size: 1.4rem; }
.reviews__stars { display: inline-flex; gap: 0.1rem; font-size: 1.1rem; }
.reviews__count { color: var(--muted); font-size: 0.9rem; }
.review-star { transition: color 0.1s; }
.reviews__empty { margin: 0.5rem 0 1rem; }
.reviews__list { list-style: none; margin: 0 0 1rem; padding: 0; display: grid; gap: 1rem; }
.review-item { padding: 0.9rem; background: var(--surfaceAlt); border: 1px solid var(--line); border-radius: var(--radius-table, 0.75rem); }
.review-item__meta { display: flex; align-items: center; flex-wrap: wrap; gap: 0.4rem 0.75rem; margin-bottom: 0.4rem; }
.review-item__stars { display: inline-flex; gap: 0.05rem; font-size: 0.95rem; }
.review-item__author { font-size: 0.95rem; }
.review-item__date { font-size: 0.8rem; color: var(--muted); margin-left: auto; }
.review-item__body { margin: 0; font-size: 0.95rem; }
.review-item__verified {
  display: inline-flex; align-items: center;
  padding: 0.1rem 0.5rem; border-radius: 999px;
  font-size: 0.72rem; font-weight: 700;
  color: var(--ok);
  background: color-mix(in oklch, var(--ok) 12%, var(--surface));
}
.review-item__photos { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.5rem; }
.review-item__photos img { width: 72px; height: 72px; object-fit: cover; border-radius: 0.5rem; border: 1px solid var(--line); }
.reviews__more { margin-top: 0.25rem; background: none; border: 1px solid var(--line); border-radius: var(--radius-pill, 999px); padding: 0.45rem 1rem; cursor: pointer; color: var(--ink); font: inherit; font-size: 0.9rem; transition: border-color 150ms ease, color 150ms ease, transform 150ms ease; }
.reviews__more:hover { border-color: var(--accent); color: var(--accent); transform: translateY(-2px); }
.reviews__form-wrap { margin-top: 1.5rem; padding-top: 1.25rem; border-top: 1px solid var(--line); }
.reviews__form-title { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 1rem; }
.reviews__thanks { padding: 0.85rem 1rem; background: var(--surfaceAlt); border: 1px solid var(--line); border-radius: var(--radius-table, 0.75rem); color: var(--ok); font-weight: 600; }
.reviews__invite {
  margin: 0 0 0.75rem; padding: 0.6rem 0.9rem; border-radius: 0.6rem;
  font-size: 0.9rem; font-weight: 600; color: var(--ok);
  background: color-mix(in oklch, var(--ok) 10%, var(--surface));
  border: 1px solid color-mix(in oklch, var(--ok) 35%, var(--line));
}
.reviews__form { display: grid; gap: 0.85rem; }
.reviews__honeypot { position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden; opacity: 0; pointer-events: none; }
.review-field { display: grid; gap: 0.3rem; }
.review-field__label { font-size: 0.875rem; color: var(--muted); }
.review-field__input { border: 1px solid var(--line); border-radius: var(--radius-input, 0.5rem); background: var(--bg); color: var(--ink); padding: 0.45rem 0.65rem; font: inherit; font-size: 0.95rem; width: 100%; box-sizing: border-box; }
.review-field__input:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
.review-field__textarea { resize: vertical; min-height: 6rem; }
.review-field__file { padding: 0.45rem; }
.review-field__hint { font-size: 0.78rem; color: var(--muted); margin-top: 0.25rem; }
.review-photos-preview { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.review-photos-preview img { width: 64px; height: 64px; object-fit: cover; border-radius: 0.5rem; border: 1px solid var(--line); }
.star-picker { display: inline-flex; gap: 0.15rem; }
.star-picker__btn { background: none; border: none; font-size: 1.5rem; cursor: pointer; padding: 0.1rem 0.05rem; line-height: 1; transition: color 0.1s, transform 0.1s; }
.star-picker__btn:hover { transform: scale(1.15); }
.reviews__submit { align-self: start; display: inline-flex; align-items: center; justify-content: center; padding: 0.95rem 1.6rem; border-radius: 999px; cursor: pointer; appearance: none; font-family: var(--font-body, var(--body)); font-size: 0.78rem; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; line-height: 1; background: var(--night, var(--ink)); color: var(--foam, #ECF3F6); border: 1px solid var(--night, var(--ink)); box-shadow: 0 16px 34px -20px color-mix(in oklch, var(--ink) 60%, transparent), inset 0 0 0 1px color-mix(in oklch, var(--gold, #D8B46B) 40%, transparent); transition: transform 160ms ease, background 160ms ease; }
.reviews__submit:hover:not(:disabled) { transform: translateY(-1px); background: var(--night2, var(--ink)); border-color: var(--night2, var(--ink)); }
.reviews__submit:disabled { opacity: 0.5; cursor: not-allowed; }

.inquire { margin-top: 3rem; padding-top: 2rem; border-top: 1px solid var(--line); max-width: 42rem; }
.inquire__title { font-family: var(--font-display, var(--display)); font-size: 1.6rem; margin: 0 0 0.25rem; color: var(--ink); font-weight: 800; }
.inquire__lede  { color: var(--inkSoft); margin: 0 0 1.5rem; }
.related { background: var(--surface); border-top: 1px solid var(--line); }
.related__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem); }
</style>
